/**
 * Sidebar-specific NavigationMenu components for vertical navigation with floating panels.
 * These components extend Radix NavigationMenu for sidebar use cases.
 */
import { composeRefs } from '@radix-ui/react-compose-refs';
import * as NavigationMenuPrimitive from '@radix-ui/react-navigation-menu';
import * as React from 'react';

import { cn } from '@heidi/utils';

import {
  SIDEBAR_WIDTH,
  SIDEBAR_WIDTH_ICON,
  SIDEBAR_WIDTH_LABEL_ICONS,
} from '../page-shell/components/sidebar/sidebar.types';
import {
  navigationMenuVerticalClasses,
  sidebarNavigationMenuContentClasses,
  sidebarPanelViewportContainerClasses,
  sidebarPanelViewportInnerClasses,
  sidebarPanelViewportTransitionDefaultClasses,
  sidebarPanelViewportTransitionSwitchingClasses,
} from './navigation-menu.styles';
import type {
  NavigationMenuTriggerUnstyledProps,
  NavigationMenuVerticalProps,
  NavigationMenuVerticalTrackedProps,
  SidebarNavigationMenuContentProps,
  SidebarPanelViewportProps,
  TriggerPositionContextValue,
} from './navigation-menu.types';

// ============================================================================
// Trigger Position Context
// ============================================================================

// Context for sharing trigger position between NavigationMenu and Viewport
const TriggerPositionContext = React.createContext<TriggerPositionContextValue | null>(
  null
);

/**
 * Provider that tracks the active trigger's vertical position.
 * Wrap around NavigationMenuVerticalTracked and SidebarPanelViewport to enable
 * dynamic viewport positioning aligned with the active trigger.
 *
 * Tracks whether we're opening for the first time vs switching between items:
 * - First open (null → value): viewport snaps instantly to trigger position
 * - Switching (value1 → value2): viewport animates to new trigger position
 *
 * @example
 * ```tsx
 * <SidebarPanelProvider>
 *   <NavigationMenuVerticalTracked>
 *     <NavigationMenuList>...</NavigationMenuList>
 *   </NavigationMenuVerticalTracked>
 *   <SidebarPanelViewport />
 * </SidebarPanelProvider>
 * ```
 */
const SidebarPanelProvider = ({ children }: { children: React.ReactNode }) => {
  const [triggerTop, setTriggerTop] = React.useState<number | null>(null);
  const [isSwitching, setIsSwitching] = React.useState(false);
  // Track previous triggerTop to detect first open vs switching
  const prevTriggerTopRef = React.useRef<number | null>(null);

  // Wrapper around setTriggerTop that tracks whether we're switching
  const handleSetTriggerTop = React.useCallback((top: number | null) => {
    const wasOpen = prevTriggerTopRef.current !== null;
    const willBeOpen = top !== null;

    // We're switching if both previous and new positions are valid (item → item transition)
    setIsSwitching(wasOpen && willBeOpen);
    prevTriggerTopRef.current = top;
    setTriggerTop(top);
  }, []);

  const value = React.useMemo(
    () => ({ triggerTop, setTriggerTop: handleSetTriggerTop, isSwitching }),
    [triggerTop, handleSetTriggerTop, isSwitching]
  );

  return (
    <TriggerPositionContext.Provider value={value}>
      {children}
    </TriggerPositionContext.Provider>
  );
};
SidebarPanelProvider.displayName = 'SidebarPanelProvider';

/**
 * Hook to access trigger position context.
 * Returns null if used outside of SidebarPanelProvider.
 */
const useTriggerPosition = () => React.useContext(TriggerPositionContext);

// ============================================================================
// Vertical NavigationMenu Components
// ============================================================================

/**
 * Vertical NavigationMenu root for sidebar navigation.
 * Uses orientation="vertical" for Arrow Up/Down keyboard navigation.
 * Does NOT include a viewport - use SidebarPanelViewport separately.
 *
 * @example
 * ```tsx
 * <NavigationMenuVertical>
 *   <NavigationMenuList>
 *     <NavigationMenuItem>...</NavigationMenuItem>
 *   </NavigationMenuList>
 *   <SidebarPanelViewport />
 * </NavigationMenuVertical>
 * ```
 */
const NavigationMenuVertical = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Root>,
  NavigationMenuVerticalProps
>(({ className, children, ...props }, ref) => (
  <NavigationMenuPrimitive.Root
    ref={ref}
    orientation="vertical"
    className={cn(navigationMenuVerticalClasses, className)}
    {...props}
  >
    {children}
  </NavigationMenuPrimitive.Root>
));
NavigationMenuVertical.displayName = 'NavigationMenuVertical';

/**
 * NavigationMenuVertical root with trigger position tracking.
 * Automatically updates the trigger position context when the active item changes.
 *
 * @example
 * ```tsx
 * <SidebarPanelProvider>
 *   <NavigationMenuVerticalTracked>
 *     <NavigationMenuList>...</NavigationMenuList>
 *   </NavigationMenuVerticalTracked>
 *   <SidebarPanelViewport />
 * </SidebarPanelProvider>
 * ```
 */
const NavigationMenuVerticalTracked = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Root>,
  NavigationMenuVerticalTrackedProps
>(({ className, children, onValueChange, ...props }, ref) => {
  const rootRef = React.useRef<HTMLElement | null>(null);
  const rafIdRef = React.useRef<number | null>(null);
  const context = useTriggerPosition();

  const handleValueChange = React.useCallback(
    (value: string) => {
      // Call original handler if provided
      onValueChange?.(value);

      // Update trigger position in context
      if (!context) return;

      // Cancel any pending rAF to avoid stale updates when rapidly switching
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }

      if (value) {
        // Find the active trigger by data-state="open"
        // requestAnimationFrame ensures DOM has updated with new data-state
        rafIdRef.current = requestAnimationFrame(() => {
          rafIdRef.current = null;
          const activeTrigger = rootRef.current?.querySelector(
            '[data-radix-collection-item][data-state="open"]'
          );
          if (activeTrigger) {
            const rect = activeTrigger.getBoundingClientRect();
            context.setTriggerTop(rect.top);
          }
        });
      } else {
        context.setTriggerTop(null);
      }
    },
    [onValueChange, context]
  );

  // Cleanup pending rAF on unmount
  React.useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, []);

  return (
    <NavigationMenuPrimitive.Root
      ref={composeRefs(ref, rootRef)}
      orientation="vertical"
      className={cn(navigationMenuVerticalClasses, className)}
      onValueChange={handleValueChange}
      {...props}
    >
      {children}
    </NavigationMenuPrimitive.Root>
  );
});
NavigationMenuVerticalTracked.displayName = 'NavigationMenuVerticalTracked';

// ============================================================================
// Sidebar Panel Viewport
// ============================================================================

/**
 * Sidebar panel viewport positioned to the right of the sidebar.
 * Uses fixed positioning to render visually outside the sidebar while staying
 * in the DOM tree for proper keyboard focus management.
 * Requires sidebarState and collapsible props to calculate correct position.
 *
 * When used with SidebarPanelProvider, automatically aligns with the active trigger.
 * Falls back to sidebar header height if no trigger position is available.
 *
 * @example
 * ```tsx
 * const { state, collapsible } = useSidebar();
 * <SidebarPanelProvider>
 *   <NavigationMenuVerticalTracked>
 *     <NavigationMenuList>...</NavigationMenuList>
 *   </NavigationMenuVerticalTracked>
 *   <SidebarPanelViewport sidebarState={state} collapsible={collapsible} />
 * </SidebarPanelProvider>
 * ```
 */
const SidebarPanelViewport = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Viewport>,
  SidebarPanelViewportProps
>(({ className, sidebarState = 'expanded', collapsible = 'icon', ...props }, ref) => {
  const context = useTriggerPosition();

  // Calculate inline-start position based on sidebar state (memoized to avoid recalc)
  // Use inline-start (logical) so the viewport mirrors with the sidebar in RTL.
  const insetInlineStartPosition = React.useMemo(() => {
    if (sidebarState === 'collapsed') {
      if (collapsible === 'icon') {
        return `calc(${SIDEBAR_WIDTH_ICON} + 0.5rem)`;
      }
      if (collapsible === 'label-icons') {
        return `calc(${SIDEBAR_WIDTH_LABEL_ICONS} + 0.5rem)`;
      }
    }
    return `calc(${SIDEBAR_WIDTH} + 0.5rem)`;
  }, [sidebarState, collapsible]);

  // Use trigger position if available, otherwise fall back to sidebar header height
  const topPosition = context?.triggerTop;

  // Memoize style object to avoid recreation on every render
  // When topPosition is available, use it; otherwise fall back to header height
  const containerStyle = React.useMemo(
    () => ({
      insetInlineStart: insetInlineStartPosition,
      top:
        topPosition !== null && topPosition !== undefined
          ? `${topPosition}px`
          : 'var(--sidebar-header-height)',
    }),
    [insetInlineStartPosition, topPosition]
  );

  // Only animate top when switching between items, not on first open
  // First open: snap instantly to trigger position for better UX
  // Switching: animate smoothly between trigger positions
  const isSwitching = context?.isSwitching ?? false;
  const transitionClasses = isSwitching
    ? sidebarPanelViewportTransitionSwitchingClasses
    : sidebarPanelViewportTransitionDefaultClasses;

  // Use fixed positioning to render outside sidebar visually while keeping
  // in the DOM tree for proper keyboard focus management
  return (
    <div
      role="presentation"
      className={cn(sidebarPanelViewportContainerClasses, transitionClasses)}
      style={containerStyle}
    >
      <NavigationMenuPrimitive.Viewport
        ref={ref}
        style={{
          // CRITICAL: Override Radix's default transition to only animate opacity
          // This prevents the viewport from animating height changes which creates
          // an unwanted "sliding down" effect when opening panels near the bottom
          transition: 'opacity 200ms ease-out',
        }}
        className={cn(sidebarPanelViewportInnerClasses, className)}
        {...props}
      />
    </div>
  );
});
SidebarPanelViewport.displayName = 'SidebarPanelViewport';

// ============================================================================
// Sidebar Navigation Menu Content
// ============================================================================

/**
 * NavigationMenuContent variant for vertical sidebar navigation.
 * Uses vertical motion animations (up/down) instead of horizontal (left/right)
 * for smoother transitions when switching between vertically-stacked menu items.
 *
 * @example
 * ```tsx
 * <NavigationMenuItem>
 *   <NavigationMenuTriggerUnstyled asChild>
 *     <SidebarMenuButton>Dashboard</SidebarMenuButton>
 *   </NavigationMenuTriggerUnstyled>
 *   <SidebarNavigationMenuContent>
 *     <DashboardPanel />
 *   </SidebarNavigationMenuContent>
 * </NavigationMenuItem>
 * ```
 */
const SidebarNavigationMenuContent = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Content>,
  SidebarNavigationMenuContentProps
>(({ className, ...props }, ref) => (
  <NavigationMenuPrimitive.Content
    ref={ref}
    className={cn(sidebarNavigationMenuContentClasses, className)}
    {...props}
  />
));
SidebarNavigationMenuContent.displayName = 'SidebarNavigationMenuContent';

// ============================================================================
// Unstyled Trigger
// ============================================================================

/**
 * Unstyled NavigationMenu trigger for sidebar use.
 * Unlike NavigationMenuTrigger, this doesn't add any styling or chevron icon.
 * Use with asChild to pass all trigger props to your custom element.
 *
 * Includes explicit Space key handling to ensure keyboard accessibility when
 * the trigger child is an anchor element (Space doesn't natively activate anchors,
 * but buttons handle Space natively on keyup so we skip them to avoid double-triggering).
 *
 * Follows Radix's composeEventHandlers pattern: user's onKeyDown fires first,
 * then our handler runs if not defaultPrevented.
 *
 * @example
 * ```tsx
 * <NavigationMenuTriggerUnstyled asChild>
 *   <SidebarMenuButton>Dashboard</SidebarMenuButton>
 * </NavigationMenuTriggerUnstyled>
 * ```
 */
const NavigationMenuTriggerUnstyled = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Trigger>,
  NavigationMenuTriggerUnstyledProps
>(({ className, onKeyDown, ...props }, ref) => {
  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      // Call user's handler first (Radix composeEventHandlers pattern)
      onKeyDown?.(event);

      // Only handle Space if not already prevented and element is not a button
      // Buttons handle Space natively (on keyup), so we skip to avoid double-triggering
      // Anchors and other elements don't activate on Space, so we add that behavior
      if (event.key === ' ' && !event.defaultPrevented) {
        const target = event.currentTarget;
        const isButton =
          target.tagName === 'BUTTON' ||
          target.getAttribute('role') === 'button' ||
          target.getAttribute('type') === 'button' ||
          target.getAttribute('type') === 'submit';

        if (!isButton) {
          event.preventDefault();
          target.click();
        }
      }
    },
    [onKeyDown]
  );

  return (
    <NavigationMenuPrimitive.Trigger
      ref={ref}
      className={className}
      onKeyDown={handleKeyDown}
      {...props}
    />
  );
});
NavigationMenuTriggerUnstyled.displayName = 'NavigationMenuTriggerUnstyled';

export {
  NavigationMenuTriggerUnstyled,
  NavigationMenuVertical,
  NavigationMenuVerticalTracked,
  SidebarNavigationMenuContent,
  SidebarPanelProvider,
  SidebarPanelViewport,
  useTriggerPosition,
};
